import React, { PropsWithChildren } from 'react';
import ErrorDisplay from 'components/ui/ErrorDisplay';
import { ThemeProvider as StyledComponentThemeProvider } from 'styled-components';
import SiteFontLoader from '../SiteFontLoader';
import RealtimeSite from 'components/core/RealtimeSite';
import DocumentTitle from 'components/application/Site/DocumentTitle';
import ErrorBoundary from 'components/core/ErrorBoundary';
import ScreenOrientation from './ScreenOrientation';
import StripeElements from '../StripeElements';
import { SITES_WITHOUT_STRIPE, STRIPE_PUBLISHABLE_KEY } from 'config';
import { useDispatch, useSelector } from 'react-redux';
import { Background } from './styles';
import SiteWrapper from '../SiteWrapper';
import ModalContainer from 'components/modals/ModalContainer';
import AdminFontLoader from 'components/application/SiteFontLoader/AdminFontLoader';
import ConfirmationModal from 'components/admin2/ui/ConfirmationModal';
import TazerDetection from 'components/insights/TazerDetection';
import { ToastsContainer } from 'components/Toast';
import { ModalPortalContainer } from 'components/modals/ModalPortal';
import { getIsStripeActive, setIsStripeActive } from 'services/billing';
import { getSavedTheme } from 'services/themes';
import { getFonts, getSiteId, getStyledComponentsThemeProvider, isPageVisible as getIsPageVisible } from 'services/app/selectors';
import GlobalStyles from './GlobalStyles';
import { getShop } from 'services/shopify';
import { getIsShopifyEnabled } from 'services/feature-gate/selectors';
import { isUserLoggedIn } from 'services/auth';

const useStripeActivation = () => {
  const dispatch = useDispatch();
  const userSubscriptions = [];
  const siteId = useSelector(getSiteId);
  const isLoggedIn = useSelector(isUserLoggedIn);
  const isShopifyEnabled = useSelector(getIsShopifyEnabled);
  const shop = useSelector(getShop);
  const isStripeActive = useSelector(getIsStripeActive);

  const userHasSubscription = React.useMemo(() => {
    // this ternary operator exist because when the page loads it will always
    // change the isStripeActive to true cause subscriptions will be falsy because
    // it's empty at the beginning
    return userSubscriptions ? userSubscriptions.some(sub => (sub as any).status === 'active') : true;
  }, [userSubscriptions]);

  return React.useCallback(() => {
    const stripeNewStatus = (
      !SITES_WITHOUT_STRIPE.includes(siteId) ||
      !isLoggedIn ||
      !userHasSubscription ||
      (isShopifyEnabled && !!shop)
    );

    if (isStripeActive && !stripeNewStatus) {
      // we need to reload the page after isStripeActive change from true to false
      // to remove stripe assets from the page, and prevent it's API calls also
      // since it's already active
      window.location.reload();
    }

    if (isStripeActive !== stripeNewStatus) {
      dispatch(setIsStripeActive(stripeNewStatus));
    }
  }, [dispatch, isShopifyEnabled, isStripeActive, isLoggedIn, shop, siteId, userHasSubscription]);
};

const ThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const theme = useSelector(getStyledComponentsThemeProvider);
  return (
    <StyledComponentThemeProvider theme={theme}>
      {children}
    </StyledComponentThemeProvider>
  );
};

const Elements: React.FC<PropsWithChildren> = ({ children }) => {
  const siteFonts = useSelector(getFonts);
  const siteTheme = useSelector(getSavedTheme);
  const isStripeActive = useSelector(getIsStripeActive);

  return (
    <StripeElements
      apiKey={STRIPE_PUBLISHABLE_KEY as string}
      fonts={siteFonts}
      isActive={isStripeActive}
      theme={siteTheme}
    >
      {children}
    </StripeElements>
  );
};

const App = () => {
  const isPageVisible = useSelector(getIsPageVisible);

  const renderCrashScreen = React.useCallback(({
    error,
    errorInfo,
  }) => (
    <ErrorDisplay
      error={error}
      errorInfo={errorInfo}
      errorNameKey="SYSTEM_ERROR"
      errorSubtitleKey="CRASH_PAGE_SUBTITLE"
      errorTitleKey="CRASH_PAGE_TITLE"
      iconName="crashPage"
      includeReloadButton={true}
    />
  ), []);

  const renderNotFoundPage = React.useCallback(() => (
    <>
      <DocumentTitle title="404 Page Not Found" />
      <ErrorDisplay
        errorNameKey="404_ERROR"
        errorSubtitleKey="NOT_FOUND_PAGE_BODY"
        errorTitleKey="NOT_FOUND_PAGE_TITLE"
        iconName="notFoundPage"
      />
    </>
  ), []);

  const updateStripeActive = useStripeActivation();
  updateStripeActive();

  return (
    <ThemeProvider>
      <GlobalStyles />
      <SiteFontLoader />
      <RealtimeSite />
      {isPageVisible ? (
        <ErrorBoundary fallback={renderCrashScreen}>
          <ScreenOrientation />
          <Elements>
            <Background>
              <SiteWrapper />
              <ModalContainer />
              <ConfirmationModal />
              <TazerDetection />
              <ToastsContainer />
              <ModalPortalContainer />
              <AdminFontLoader />
            </Background>
          </Elements>
        </ErrorBoundary>
      ) : renderNotFoundPage()}
    </ThemeProvider>
  );
};

export default App;
